import { useMemo, useState } from 'react';
import { c, tbl } from '../../theme';
import { fmtCount, fmtClock, fmtPct } from './format';

// The Email page's second view: one row per inbox, sortable.
//
// The cards answer "which inbox needs someone now" — they are ordered for you
// and banded one way. This answers a different question: "how do the inboxes
// compare on one column", which is why it exists rather than being the same
// data in a grid. The old app kept both and toggled between them.
//
// Its colour rules are the old table's own (email/hooks.js), and they are NOT
// the cards' band. The cards ask whether an inbox is meeting its own target;
// the table colours each column against a fixed scale, so a glance down one
// column is comparable across inboxes with different targets:
//
//   depth          10+ amber, 20+ red
//   oldest         over 4h amber, over a day red
//   SLA%           against that inbox's own target, 10 points under it amber
//   row tint       depth 20+ or older than two days severe; 10+ or a day warn
//
// An inbox with two emails waiting and one of them twenty-four days old is
// severe on age alone — either can signal a backlog, so either earns the tint.

const HOUR = 3600;
const DAY = 86400;

const COLUMNS = [
  { key: 'name', label: 'Inbox', num: false },
  { key: 'depth', label: 'Depth', num: true },
  { key: 'oldestSecs', label: 'Oldest pending', num: true },
  { key: 'sla', label: 'SLA%', num: true },
  { key: 'slaSeconds', label: 'SLA target', num: true },
  { key: 'handledToday', label: 'Handled today', num: true },
  { key: 'ahtToday', label: 'AHT', num: true },
];

const LABEL = Object.fromEntries(COLUMNS.map((col) => [col.key, col.label]));

const depthTone = (d) => (d == null ? c.textFaint : d >= 20 ? c.dangerText : d >= 10 ? c.warningText : c.textStrong);
const oldestTone = (s) => (s == null ? c.textFaint : s > DAY ? c.dangerText : s > 4 * HOUR ? c.warningText : c.textStrong);
const slaTone = (pct, target) => {
  const t = target ?? 80;
  return pct >= t ? c.successText : pct >= t - 10 ? c.warningText : c.dangerText;
};
const severity = ({ depth, oldestSecs }) => {
  const d = depth ?? 0;
  const o = oldestSecs ?? 0;
  if (d >= 20 || o > 2 * DAY) return 'severe';
  if (d >= 10 || o > DAY) return 'warn';
  return '';
};
const rowBg = (sev, i) => (sev === 'severe' ? c.dangerTint : sev === 'warn' ? c.warningTint
  : i % 2 ? c.surfaceSunk : c.surface);

export default function EmailInboxTable({ inboxes, fmtAge, fmtTarget, showAddresses = false }) {
  const [sortKey, setSortKey] = useState('depth');
  const [sortDir, setSortDir] = useState('desc');

  const sorted = useMemo(() => {
    const col = COLUMNS.find((x) => x.key === sortKey);
    const list = [...inboxes];
    if (!col?.num) {
      list.sort((a, b) => {
        const r = String(a[sortKey] || '').localeCompare(String(b[sortKey] || ''));
        return sortDir === 'asc' ? r : -r;
      });
      return list;
    }
    // Nulls last in BOTH directions: an inbox with no SLA% has not gone quiet
    // in a good way or a bad way, and it must not head the list when the
    // column is sorted ascending.
    list.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return list;
  }, [inboxes, sortKey, sortDir]);

  // A name reads A→Z first; a metric reads worst-first, which is the reason
  // someone clicked it.
  const sortBy = (col) => {
    if (col.key === sortKey) return setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    setSortKey(col.key);
    setSortDir(col.num ? 'desc' : 'asc');
  };

  return (
    <div>
      <p style={s.note}>
        {sorted.length} inbox{sorted.length === 1 ? '' : 'es'} · sorted by {LABEL[sortKey]} {sortDir === 'asc' ? '↑' : '↓'}
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table style={s.table}>
          <thead>
            <tr>
              {COLUMNS.map((col) => (
                <th key={col.key} style={col.num ? s.thNum : s.th}
                  aria-sort={col.key === sortKey ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                  <button type="button" onClick={() => sortBy(col)} style={col.num ? s.sortBtnNum : s.sortBtn}>
                    {col.label}{col.key === sortKey ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((r, i) => {
              const sev = severity(r);
              return (
                <tr key={r.queueId} style={{ background: rowBg(sev, i) }}>
                  <td style={s.td} title={r.email || undefined}>
                    <div style={s.name}>{r.name || '—'}</div>
                    {showAddresses && r.email && <div style={s.address}>{r.email}</div>}
                  </td>
                  <td style={{ ...s.num, color: depthTone(r.depth), fontWeight: (r.depth ?? 0) >= 10 ? 600 : 400 }}>
                    {r.depth == null ? '—' : fmtCount(r.depth)}
                  </td>
                  <td style={{ ...s.num, color: oldestTone(r.oldestSecs), fontWeight: (r.oldestSecs ?? 0) > DAY ? 600 : 400 }}>
                    {fmtAge(r.oldestSecs)}
                  </td>
                  <td style={s.num}>
                    {r.sla == null ? <span style={{ color: c.textFaint }}>—</span>
                      : <span style={{ color: slaTone(r.sla, r.slaPct), fontWeight: 600 }}>{fmtPct(r.sla)}</span>}
                  </td>
                  {/* The anchor: always present, so an empty SLA% beside it
                      reads as "nothing completed yet", not as a broken feed. */}
                  <td style={{ ...s.num, color: c.textMuted }}>
                    {r.slaPct == null ? '—' : `${Math.round(r.slaPct)}% / ${fmtTarget(r.slaSeconds)}`}
                  </td>
                  <td style={s.num}>{fmtCount(r.handledToday ?? 0)}</td>
                  <td style={s.num}>
                    {r.ahtToday == null ? <span style={{ color: c.textFaint }}>—</span> : fmtClock(r.ahtToday)}
                  </td>
                </tr>
              );
            })}
            {!sorted.length && (
              <tr><td colSpan={COLUMNS.length} style={s.empty}>No inbox matches.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const s = {
  note:       { fontSize: '11px', color: c.textMuted, margin: '0 0 8px' },
  table:      { width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' },
  th:         { ...tbl.th, textAlign: 'left', whiteSpace: 'nowrap', padding: 0 },
  thNum:      { ...tbl.thR, whiteSpace: 'nowrap', padding: 0 },
  sortBtn:    { all: 'unset', cursor: 'pointer', display: 'block', width: '100%',
    padding: '8px 10px', textAlign: 'left', font: 'inherit' },
  sortBtnNum: { all: 'unset', cursor: 'pointer', display: 'block', width: '100%',
    padding: '8px 10px', textAlign: 'right', font: 'inherit' },
  td:         { ...tbl.td, color: c.textStrong },
  num:        { ...tbl.tdR, color: c.textStrong },
  name:       { fontWeight: 600, color: c.text },
  address:    { fontSize: '11px', color: c.textFaint },
  empty:      { ...tbl.td, textAlign: 'center', color: c.textMuted, padding: '20px' },
};
